<script lang="ts">
  /**
   * Full-screen maintenance notice.
   *
   * Rendered by the root layout in place of the ENTIRE app whenever
   * `MAINTENANCE_MODE` (src/lib/maintenance.ts) is true. Because it replaces
   * the whole app tree, there are no trade controls anywhere to reach —
   * trading is fully paused while this is showing.
   *
   * Styled with the app's own language (Stage backdrop + glass card, Söhne
   * type, Material Icons Round, --warn amber) so it reads as part of
   * TickEdge rather than a generic error page.
   */
  import Stage from '@/components/Stage.svelte';
</script>

<Stage />

<main class="maint" role="status" aria-live="polite">
  <div class="card glass">
    <img class="logo" src="/logo_dark.svg" alt="TickEdge" />

    <div class="badge" aria-hidden="true">
      <span class="material-icons-round">construction</span>
    </div>

    <div class="copy">
      <h1>We&rsquo;ll be right back</h1>
      <p>
        TickEdge is undergoing scheduled maintenance and will be back online
        shortly. Trading is temporarily paused &mdash; thank you for your
        patience.
      </p>
    </div>

    <div class="pill">
      <span class="dot">
        <span class="ping"></span>
        <span class="core"></span>
      </span>
      <span class="pill-label">Maintenance in progress</span>
    </div>
  </div>
</main>

<style>
  .maint {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 64px 24px;
    position: relative;
    z-index: 1;
  }

  .card {
    width: 100%;
    max-width: 440px;
    border-radius: 22px;
    padding: 40px 32px 34px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 24px;
  }

  .logo {
    height: 26px;
    width: auto;
    display: block;
  }

  .badge {
    display: grid;
    place-items: center;
    height: 64px;
    width: 64px;
    border-radius: 18px;
    background: rgba(255, 185, 74, 0.1);
    color: var(--warn);
  }
  .badge :global(.material-icons-round) {
    font-size: 30px;
  }

  .copy {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 34ch;
  }
  .copy h1 {
    font-size: 26px;
    font-weight: 700;
    color: var(--text-0);
    letter-spacing: -0.015em;
    line-height: 1.15;
  }
  .copy p {
    font-size: 14px;
    color: var(--text-2);
    line-height: 1.55;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 13px;
    border-radius: 999px;
    border: 1px solid var(--glass-border);
    background: rgba(255, 255, 255, 0.03);
  }
  .pill-label {
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-2);
  }

  .dot {
    position: relative;
    display: inline-flex;
    height: 8px;
    width: 8px;
  }
  .ping {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: var(--warn);
    opacity: 0.75;
    animation: maint-ping 1.4s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
  .core {
    position: relative;
    display: inline-flex;
    height: 8px;
    width: 8px;
    border-radius: 999px;
    background: var(--warn);
  }

  @keyframes maint-ping {
    75%,
    100% {
      transform: scale(2.2);
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .ping {
      animation: none;
    }
  }
</style>
